import { useState } from 'react';
import useCSVHandler from './UseCSVHandler';
import { TEMPLATE_FORM_CSV_HEADERS, convertCSVToTemplateSections, exportCSV } from '../utils/templateFormCsv';
import { mergeCsvTemplateConfiguration } from '../utils/templateFormCsvMerge';

const useTemplateFormFieldsHandler = () => {
    const [errors, setErrors] = useState<string[]>([]);
    const { importedData, importCSV } = useCSVHandler();

    const exportTemplateFormFields = (templateConfiguration: any, templateName?: string) => {
        try {
            setErrors([]); // Clear previous errors

            if (!templateConfiguration) {
                setErrors(['No template configuration available for export. Please refresh the page to start over.']);
                return;
            }

            const exportData = [];

            // Extract all form fields from all sections
            templateConfiguration?.templateForm?.templateFormSections?.forEach(section => {
                section.templateFormFields?.forEach(field => {
                    try {
                        exportData.push({
                            sectionId: section.id ?? null,
                            sectionTitle: section.title,
                            sectionPriority: section.priority,
                            fieldId: field.id ?? null,
                            fieldTitle: field.title,
                            fieldLabel: field.label || '', // Add the label field for "Text Field Label"
                            fieldType: field.fieldType,
                            associatedVariable: field.associatedVariable,
                            placeholder: field.placeholder,
                            tip: field.tip,
                            defaultValue: field.defaultValue,
                            conditionalDefaults: field.conditionalDefaults ? JSON.stringify(field.conditionalDefaults) : '',
                            options: field.options ? JSON.stringify(field.options) : '',
                            isRequired: field.isRequired,
                            priority: field.priority
                        });
                    } catch (fieldError) {
                        setErrors(prev => [...prev, `Error processing field "${field.title}": ${fieldError.message}`]);
                    }
                });
            });

            if (exportData.length === 0) {
                setErrors(['No form fields to export. Please add some form fields to your template before exporting.']);
                return;
            }

            // Generate filename
            const name = templateName || 'template';
            const date = new Date();
            const formattedDate = `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
            const formattedTime = `${date.getHours()}-${date.getMinutes()}`;
            const filename = `${name.toLowerCase().replace(/\s+/g, '-')}-form-fields-${formattedDate}-${formattedTime}`;

            exportCSV(exportData, TEMPLATE_FORM_CSV_HEADERS, filename);

        } catch (error) {
            setErrors([`Export failed: ${error.message}. Please try again or refresh the page to start over.`]);
        }
    };

    const importTemplateFormFields = (file: File) => {
        setErrors([]); // Clear previous errors

        try {
            importCSV(file, csvArray => {
                const { sections, errors: importErrors } = convertCSVToTemplateSections(csvArray);
                if (importErrors.length > 0) {
                    setErrors(importErrors);
                }

                return sections;
            });
        } catch (error) {
            setErrors([`Failed to start import process: ${error.message}. Please try again or refresh the page to start over.`]);
        }
    };

    const mergeTemplateSections = (importedSections: any[], existingTemplate: any) => {
        const { templateConfiguration, errors: mergeErrors } = mergeCsvTemplateConfiguration(existingTemplate, importedSections);
        if (mergeErrors.length > 0) {
            setErrors(mergeErrors);
        }

        return templateConfiguration;
    };

    return {
        importedData,
        errors,
        exportTemplateFormFields,
        importTemplateFormFields,
        mergeTemplateSections,
        setErrors
    };
};

export default useTemplateFormFieldsHandler;
